import type { Phase, Priority, Project, Resource } from "../domain/types.js";
import { currentAccessToken } from "./session.js";
import type { ScenarioRow } from "../db/queries.js";
import type { ImportTargetCandidate } from "../domain/import/target.js";

/** The caller, as the server resolved them. Mirrors ports/AuthedUser. */
export interface BoardUser {
  userId: string;
  role: "engineer" | "manager" | "admin";
  resourceId: string | null;
  teamId: string | null;
  managedTeamIds: string[];
  /** Project types (Epicor product codes) owned by teams this person manages. */
  managedProjectTypes?: string[];
  /** Set when a real admin is previewing the board as this person. */
  viewingAs?: { resourceId: string; name: string } | null;
}

export interface ScheduledRow {
  assignmentId: string;
  phaseId: string;
  resourceId: string | null;
  queuePosition: number | null;
  priority: Priority;
  startBlock: number | null;
  endBlock: number | null;
  halfDayBlocks: number;
  pinned: boolean;
  frozen: boolean;
  startDate: string | null;
  endDate: string | null;
}

export interface Team {
  teamId: string;
  name: string;
  managerResourceId: string | null;
  projectTypes: string[];
}

export interface AppUser {
  userId: string;
  name: string | null;
  email: string | null;
  role: "engineer" | "manager" | "admin";
  /** A role parked before this person ever signed in. */
  pending: boolean;
  resourceId: string | null;
  resourceName: string | null;
}

export const fetchUsers = () => get<{ users: AppUser[] }>("/users");

export interface Board {
  scenarioId: string;
  scenarios: ScenarioRow[];
  today: string;
  fromBlock: number;
  projects: Project[];
  phases: (Phase & { remainingHours: number; overEstimate?: boolean })[];
  resources: Resource[];
  teams: Team[];
  assignments: ScheduledRow[];
  utilization: { resourceId: string; weekStart: string; bookedHours: number; capacityHours: number; pct: number }[];
  pressure: { resourceId: string; committedHours: number; capacityHours: number; pct: number; lateHours: number }[];
  /** Days from today on when someone is out: halfDays 0 is the whole day, 1 half of it. */
  timeOff: { resourceId: string; day: string; halfDays: 0 | 1 }[];
  kpis: {
    remainingHours: number;
    workbookHours: number;
    overstatedBy: number;
    openPhases: number;
    projects: number;
    late: number;
    unassigned: number;
    unassignedHours: number;
  };
  user: BoardUser;
}

export const TENANT = Number(import.meta.env.VITE_SENTRY_TENANT ?? 6);

/**
 * True unless the build was explicitly stamped "prod" — see DEPLOY.md: dev
 * and prod share the one Supabase project for the sentry schema, so a build
 * with no environment baked in (a stale image, a local build, a step someone
 * forgot) is the case most likely to actually be writing to production, not
 * the one safe to stay quiet about.
 */
export const IS_DEV_DB = (import.meta.env.VITE_SENTRY_ENV ?? "dev") !== "prod";

/**
 * In dev the Vite proxy forwards /v1 to the backend, so a relative path works.
 * In production the UI and the API are separate Cloud Run services, so the
 * origin has to be baked in at build time — same pattern as the cockpit.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
export const apiUrl = (path: string) => `${API_BASE}${path}`;

/**
 * Which resourceId, if any, an admin is currently previewing the board as.
 *
 * In memory only — never localStorage — so a reload always comes back as
 * your own real identity rather than leaving a stale impersonation active
 * for whoever opens the tab next.
 */
let viewAsResourceId: string | null = null;
export const getViewAs = () => viewAsResourceId;
export const setViewAs = (resourceId: string | null) => { viewAsResourceId = resourceId; };

/**
 * Every call to the API goes through here so the bearer token is attached in
 * exactly one place. Calling `fetch(apiUrl(...))` directly still compiles but
 * sends no credentials, which the server reads as an anonymous — read-only —
 * caller, so a missed call site fails as a 403 on write rather than silently.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // Asked for per call: a cached token is empty until the stored session loads
  // and stale once it expires, and both look like silently lost permissions.
  const token = await currentAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (viewAsResourceId) headers.set("x-sentry-view-as", viewAsResourceId);
  return fetch(apiUrl(path), { ...init, headers });
}

export async function fetchBoard(scenarioId?: string): Promise<Board> {
  const qs = new URLSearchParams({ tenantId: String(TENANT) });
  if (scenarioId) qs.set("scenarioId", scenarioId);
  const res = await apiFetch(`/v1/sentry/board?${qs}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
  return res.json();
}

export interface MoveResult {
  /** Present when the move reassigned work, which re-solves the scenario. */
  requirements?: unknown[];
  churn?: number;
  /** True when the re-solve was held: the move and its requirement are saved, the solve is not. */
  held?: boolean;
}

/**
 * Solve a draft now, after moves made while the solve was held. A refusal
 * (conflicting requirements, not your draft) throws with the server's message.
 */
export async function solveScenario(scenarioId: string): Promise<MoveResult> {
  const res = await apiFetch(`/v1/sentry/scenarios/${scenarioId}/solve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tenantId: TENANT }),
  });
  const body = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body as MoveResult;
}

/**
 * Move a phase, and — when the move reassigns it — carry back the re-solve.
 *
 * A reassignment is recorded as a requirement server-side, so the response is
 * the whole requirement list and the churn, not an acknowledgement. A refusal
 * still throws: the message names the rule or the conflicting requirements,
 * and the caller shows it.
 */
export async function moveAssignment(
  assignmentId: string,
  patch: { resourceId?: string | null; queuePosition?: number; priority?: Priority; scenarioId?: string; hold?: boolean },
): Promise<MoveResult> {
  const res = await apiFetch(`/v1/sentry/assignments/${assignmentId}/move`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tenantId: TENANT, ...patch }),
  });
  const body = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body as MoveResult;
}

// ── the maintenance surfaces ─────────────────────────────────────────

/**
 * One request, one JSON body, one thrown error.
 *
 * Every write on the maintenance screens has the same shape, and doing it by
 * hand in each component is how a call site quietly loses its bearer token.
 */
export async function send<T = any>(
  path: string, method: string, body?: unknown,
): Promise<T> {
  const res = await apiFetch(`/v1/sentry${path}`, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tenantId: TENANT, ...(body as object) }),
  });
  const json = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return json as T;
}

export async function get<T = any>(path: string): Promise<T> {
  const join = path.includes("?") ? "&" : "?";
  const res = await apiFetch(`/v1/sentry${path}${join}tenantId=${TENANT}`);
  const json = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return json as T;
}

export interface Skill {
  skillId: string;
  name: string;
  category: string | null;
}

export interface ResourceSkill {
  resourceId: string;
  skillId: string;
  proficiency: number;
  inferred: boolean;
}

export interface TemplateStep {
  seq: number;
  phaseCode: string | null;
  workType: string;
  discipline: string | null;
  estHours: number;
  skillId: string | null;
  minProficiency: number | null;
  dueOffsetDays: number;
  dependsOnSeq: number | null;
}

export interface BreakdownTemplate {
  templateId: string;
  name: string;
  productCode: string | null;
  active: boolean;
  steps: TemplateStep[];
}

export interface WorkTypeRow {
  code: string;
  label: string;
  note: string | null;
  sortOrder: number;
  active: boolean;
}

export interface WorkflowStateRow {
  stateId: string;
  label: string;
  isTerminal: boolean;
  countsAsWip: boolean;
  sortOrder: number;
}

export interface BreakdownData {
  projects: Project[];
  /**
   * ownerTeamId is read from the published schedule only (never a draft) —
   * same as the server's deny() check — so the screen can enable a row the
   * same way the server would allow editing it.
   */
  phases: (Phase & { remainingHours: number; ownerTeamId: string | null })[];
  resources: Resource[];
  skills: Skill[];
  resourceSkills: ResourceSkill[];
  templates: BreakdownTemplate[];
  workTypes: WorkTypeRow[];
  workflowStates: WorkflowStateRow[];
  today: string;
  user: BoardUser;
}

export const fetchBreakdown = () => get<BreakdownData>("/breakdown");

export interface ImportPlan {
  newProjects: {
    projectId: string; description: string | null; productCode: string | null;
    dueDate: string | null; template: { templateId: string; name: string } | null;
  }[];
  dueDateMoves: { projectId: string; from: string | null; to: string; phasesAffected: number }[];
  headerChanges: { projectId: string; field: string; from: unknown; to: unknown }[];
  vanished: { projectId: string; lastSeen: string | null; openPhases: number }[];
  returned: string[];
  unchanged: number;
}

export interface ImportPreview {
  rowCount: number;
  plan: ImportPlan;
  empty: boolean;
  /** Today's draft, `Day YYYY-MM-DD` on the server's clock — what a blank name means. */
  defaultScenarioName: string;
  /** Drafts a person may name as the target, offered as suggestions. */
  draftScenarioNames: string[];
  /** Every scenario, for the same target decision the apply makes. */
  scenarios: ImportTargetCandidate[];
  /**
   * The plan's fingerprint. Sent back with the apply, which answers 409
   * `stale_preview` and writes nothing if its own plan no longer matches.
   */
  planHash: string;
  /**
   * When each project coming back was last seen, by id. Optional: a server
   * older than the screen sends none, and the dates then show as —.
   */
  returnedLastSeen?: Record<string, string | null>;
  /** Archived projects among the vanished and returned — finished work, not a warning. */
  archivedIds?: string[];
}

/** What either import apply answers. */
export interface ImportApplyResult {
  batchId: string;
  scenarioId: string | null;
  /** Which scenario the changes went into, by name; null when nothing changed. */
  scenarioName: string | null;
  scenarioCreated: boolean;
  applied: number;
}

/** Past this share of the active portfolio going missing, the preview warns before apply. */
export interface VanishBrake {
  tripped: boolean;
  vanishing: number;
  active: number;
  share: number;
}

/**
 * What `/import/epicor/preview` answers: the same preview, plus when it was
 * pulled and the snapshot it kept — which the apply names, so it writes these
 * rows rather than pulling again.
 */
export interface EpicorImportPreview extends ImportPreview {
  source: "epicor";
  pulledAt: string;
  snapshotId: string;
  brake: VanishBrake;
}

type ImportSource = "file" | "epicor";
/** `undone`: its changes were taken back by hand afterwards, so it no longer counts as an import. */
type ImportBatchStatus = "running" | "applied" | "failed" | "undone";

export interface ImportBatch {
  batchId: string;
  filename: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  rowCount: number | null;
  scenarioId: string | null;
  source: ImportSource;
  status: ImportBatchStatus;
  error: string | null;
  changes: number;
}

/** A value a past import never recorded. An object, so no real value can be mistaken for it. */
export type Unknown = { unknown: true };
export type Unknowable<T> = T | Unknown;
export const isUnknown = (v: unknown): v is Unknown =>
  typeof v === "object" && v !== null && (v as { unknown?: unknown }).unknown === true;

/**
 * The plan a reopened import draws. A saved plan is the preview's `ImportPlan`
 * exactly, and fits this with nothing unknown; one rebuilt from an older
 * batch's change log is unknown wherever that log is silent.
 */
export interface PastImportPlan {
  newProjects: {
    projectId: string; description: string | null;
    productCode: Unknowable<string | null>; dueDate: Unknowable<string | null>;
    template: Unknowable<{ templateId: string; name: string } | null>;
  }[];
  dueDateMoves: { projectId: string; from: string | null; to: string | null; phasesAffected: Unknowable<number> }[];
  headerChanges: { projectId: string; field: string; from: unknown; to: unknown }[];
  vanished: { projectId: string; lastSeen: string | null; openPhases: Unknowable<number> }[];
  returned: Unknowable<string[]>;
  unchanged: Unknowable<number>;
}

export interface PastImportBatch extends Omit<ImportBatch, "changes"> {
  /** Null when the scenario it went into has since been deleted, or there never was one. */
  scenarioName: string | null;
  /** When Epicor was read, for a pull that kept a snapshot. */
  pulledAt: string | null;
  uploadedByEmail: string | null;
  planHash: string | null;
  /** The vanish brake an Epicor preview showed, as the apply saved it; null for a file, or before it was kept. */
  brake: VanishBrake | null;
  /** False for a batch from before plans were saved: its plan was rebuilt from the change log. */
  planRecorded: boolean;
}

/** What `GET /import/batches/:id` answers. */
export interface PastImport {
  batch: PastImportBatch;
  plan: PastImportPlan;
  changes: { changeId: string; projectId: string; kind: string; field: string | null; before: string | null; after: string | null }[];
}

export interface AbsenceRange {
  resourceId: string;
  resourceName: string;
  from: string;
  to: string;
  days: number;
  halfDays: number;
  reason: string | null;
  past: boolean;
  /** Set when a scenario requirement recorded this, and would undo it. */
  heldBy: { scenarioId: string; scenarioName: string; label: string } | null;
}

export const fetchAbsences = () =>
  get<{ today: string; absences: AbsenceRange[] }>("/absences");

export interface FeedbackEntry {
  feedbackId: string;
  userId: string;
  authorName: string | null;
  authorEmail: string | null;
  message: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedByName: string | null;
}

/** Open to anyone signed in — the server attaches who sent it, not the caller. */
export const sendFeedback = (message: string) => send<{ ok: true; feedbackId: string }>("/feedback", "POST", { message });

/** 403s for an Engineer — callers only fetch this once they know the viewer is a manager or admin. */
export const fetchFeedback = () => get<{ feedback: FeedbackEntry[] }>("/feedback");

/** Toggle a feedback item between addressed and open. Same viewers as `fetchFeedback`. */
export const setFeedbackResolved = (feedbackId: string, resolved: boolean) =>
  send<{ ok: true }>(`/feedback/${feedbackId}`, "PATCH", { resolved });

export interface DayRequirement {
  requirementId: string;
  seq: number;
  label: string | null;
  intentText: string;
  enabled: boolean;
  origin: string;
}

export interface TodayData {
  today: string;
  lastImport: ImportBatch | null;
  lastImportAny: ImportBatch | null;
  /** The one draft everything today accumulates into. Absent until something happens. */
  dayScenario: { scenarioId: string; name: string; requirements: DayRequirement[] } | null;
  people: { resourceId: string; name: string }[];
  awaitingReview: { scenarioId: string; name: string } | null;
  needsBreakdown: {
    projectId: string; description: string | null;
    productCode: string | null; dueDate: string | null;
  }[];
  missingFromFile: { projectId: string; description: string | null; lastSeen: string | null }[];
  atRisk: {
    projectId: string; description: string | null;
    dueDate: string; projectedFinish: string;
  }[];
  away: { resourceId: string; name: string; days: string[] }[];
  /** Who is out today; halfDay means only part of it. */
  outToday: { resourceId: string; name: string; halfDay: boolean }[];
  /** Open steps at or past their estimate, shop-wide. */
  overEstimate: number;
  user: BoardUser;
}

export const fetchToday = () => get<TodayData>("/today");

// ── compare: the file's plan against the optimizer's ─────────────────

export interface CompareChange {
  phaseId: string;
  projectId: string | null;
  kinds: string[];
  from: { resourceId: string | null; start: string | null; end: string | null; position: number | null; priority: string | null };
  to: { resourceId: string | null; start: string | null; end: string | null; position: number | null; priority: string | null };
  /** Working-day shift of the finish, positive = later. */
  endShiftBlocks: number | null;
  remainingHours: number;
}

/** One term the solver had to alter to fit its fixed-point arithmetic. */
export interface ObjectiveNote {
  kind: string;
  weight: number;
  action: string;
  detail: string;
}

export interface CompareProjectImpact {
  projectId: string;
  fromEnd: string | null;
  toEnd: string | null;
  shiftBlocks: number;
  phasesChanged: number;
}

export interface CompareResult {
  diff: {
    changes: CompareChange[];
    projects: CompareProjectImpact[];
    summary: {
      reassigned: number;
      rescheduled: number;
      reorderedOnly: number;
      added: number;
      removed: number;
      projectsEarlier: number;
      projectsLater: number;
      hoursMoved: number;
    };
  };
  /** resourceId to display name, including people the system does not know. */
  names: Record<string, string>;
  summaryCounts: { jobsInFile: number; compared: number; people: number };
  /** Names in the file with no usable People record — run on shop defaults. */
  assumed: string[];
  /** Names the file assigns work to whose People record says inactive. The
   *  file wins — this says the People screen is stale, nothing more. */
  markedInactive: string[];
  /** Names the file puts in more than one drafting group. */
  multiDiscipline: string[];
  /** Rows that could not become tasks, and why. */
  skipped: { phaseId: string; why: string }[];
  /** OPTIMAL or FEASIBLE, per solve. A 10s cap binds on a backlog this size,
   *  so FEASIBLE is the best the solver found in the budget, not the best
   *  there is — a second run may find a different set of moves of the same
   *  quality. */
  solverStatus: { held: string; optimized: string };
  /** How far the file's own plan had to stretch the horizon to be dated at all. */
  horizonBlocks: number;
  peakHeldBlocks: number;
  /** Non-empty means the solver had to alter a weight to fit its fixed-point
   *  arithmetic, so the objective that ran is not quite the one asked for. */
  objectiveNotes: ObjectiveNote[];
  /** True when a job's modeled length depends on who is holding it — where
   *  capacities differ, part of any "finishes earlier" is a modeling
   *  artifact rather than a real gain. Show the caveat only when this is true. */
  durationDependsOnAssignee: boolean;
}

/**
 * A refusal that carries its own diagnosis.
 *
 * `send()` only surfaces `json.error` — right for every other write on these
 * screens, where a refusal is one sentence and nothing else. Here a refusal
 * over the file's own plan comes with numbers that explain WHY (how much
 * room the plan needed against how much it got), and those would be silently
 * dropped by the shared helper. So compare does its own fetch and keeps them
 * on the thrown error, when present, without changing what any other caller
 * of `send()` sees.
 */
export interface CompareRefusal extends Error {
  horizonBlocks?: number;
  peakHeldBlocks?: number;
  maxBlocks?: number;
}

export async function runCompare(contentBase64: string, filename: string): Promise<CompareResult> {
  const res = await apiFetch("/v1/sentry/compare", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tenantId: TENANT, contentBase64, filename }),
  });
  const json = await res.json().catch(() => ({} as any));
  if (!res.ok) {
    const err = new Error(json.error ?? `HTTP ${res.status}`) as CompareRefusal;
    if (typeof json.horizonBlocks === "number") err.horizonBlocks = json.horizonBlocks;
    if (typeof json.peakHeldBlocks === "number") err.peakHeldBlocks = json.peakHeldBlocks;
    if (typeof json.maxBlocks === "number") err.maxBlocks = json.maxBlocks;
    throw err;
  }
  return json as CompareResult;
}

/** The file as base64. Express caps a body at 5 MB, so ~3.7 MB of workbook. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`could not read ${file.name}`));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

export async function createScenario(name: string, basedOn?: string): Promise<string> {
  const res = await apiFetch(`/v1/sentry/scenarios`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tenantId: TENANT, name, basedOn }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
  return (await res.json()).scenarioId;
}

export interface PlanResult {
  ok: boolean;
  /** The scenario the requirement landed in — always set when ok. */
  scenarioId?: string;
  requirements?: unknown[];
  /** Short sentence for the toast: what changed, or why nothing did. */
  note?: string;
  error?: string;
  infeasible?: boolean;
}

/**
 * Add a requirement to a scenario and re-solve it.
 *
 * One place interprets the /ask response, because three callers need the same
 * reading of it — the new-scenario dialog, the requirement composer, and ⌘K —
 * and the interesting outcomes are not the HTTP status. A plan can be refused
 * for four different reasons that all arrive as 200 or 422, and each of them
 * needs different words.
 */
export async function planIntoScenario(scenarioId: string | undefined, intent: string): Promise<PlanResult> {
  const res = await apiFetch("/v1/sentry/ask", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // Both of this function's surfaces are plan-only boxes: there is no
    // question a person can ask from the requirement composer or the
    // new-scenario dialog. Asserting the destination skips the routing model
    // call and, more to the point, makes a misroute impossible — a sentence
    // classified `query` here fell through to "The planner produced no
    // schedule", and NewScenario had by then already made the scenario the
    // user was left holding empty.
    body: JSON.stringify({ tenantId: TENANT, intent, scenarioId, route: "plan" }),
  });
  const j = await res.json().catch(() => ({} as any));

  if (res.status === 403) {
    // The server's refusal is a full sentence naming the rule that fired — a
    // refusal is information, not a failure, and the UI shows it verbatim,
    // same as every other call site. The fallback only covers a bodiless 403
    // and must stay role-free: this call can be refused for reasons that have
    // nothing to do with the caller's session (e.g. only an admin may publish).
    return { ok: false, error: j.error ?? "That was refused." };
  }
  if (res.status === 409) return { ok: false, error: j.error };
  if (j.emptySelectors?.length) {
    return {
      ok: false,
      error: `Nothing matched ${j.emptySelectors.map((e: any) => JSON.stringify(e.select)).join(", ")}. ${j.hint ?? ""}`,
    };
  }
  if (j.issues?.length) return { ok: false, error: j.issues.map((i: any) => i.message).join(" · ") };
  if (!res.ok) return { ok: false, error: j.error ?? `HTTP ${res.status}` };

  if (j.outcome?.status === "INFEASIBLE") {
    return {
      ok: true,
      infeasible: true,
      scenarioId: j.scenarioId,
      requirements: j.requirements,
      note:
        "Those requirements cannot all hold at once" +
        (j.outcome.infeasibleCore?.length ? `: ${j.outcome.infeasibleCore.join(" · ")}` : ".") +
        " The schedule is unchanged and the new one is in the list, switched off.",
    };
  }
  if (!j.scenarioId) return { ok: false, error: j.error ?? "The planner produced no schedule." };

  const unmapped = j.unmapped?.length
    ? ` Could not express: ${j.unmapped.map((u: string) => `“${u}”`).join(", ")}.`
    : "";
  const note = j.explanation ?? `Re-solved — ${j.outcome?.churn ?? 0} phases changed hands.`;
  // The explainer is given the absences and usually states them, but "usually"
  // is not good enough for a change to the shared calendar. Add any it left out.
  const missed = ((j.absences ?? []) as { summary: string }[])
    .map((a) => a.summary)
    .filter((a) => !note.includes(a));
  return {
    ok: true,
    scenarioId: j.scenarioId,
    requirements: j.requirements,
    note: (missed.length ? `${missed.join(" ")} ${note}` : note) + unmapped,
  };
}
